import { useState } from "react";
import { AuditRow } from "@/components/desk/DeskAudit";
import PayoffDiagram from "@/components/PayoffDiagram";
import { QueryBoundary } from "@/lib/queryState";
import { Badge, Blotter, DataStateBadge, DecisionBadge, Icon, Num, RuleResult, fmt, normDecision, shortId } from "@/sentinel";

const STRUCTS = { A: "Bear put", B: "Bull call", C: "Bull put credit" };
const GATES = { PASS_LETC: "Passes L/E/T/C", WAIT: "WAIT (C fail)", NO_TRADE: "NO TRADE (L/E/T fail)" };

/** Native select in the sn-field anatomy, so the data-testid stays on the <select>. */
export function FilterSelect({ label, value, onChange, options, testId, minWidth = 140 }) {
  return (
    <label className="sn-field" style={{ minWidth }}>
      <span className="sn-field-label">{label}</span>
      <span className="sn-input-wrap">
        <select className="sn-input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} style={{ cursor: "pointer" }}>
          {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </span>
    </label>
  );
}

/** Gate result for a row: PASS for L/E/T/C, otherwise the muted decision it maps to. Never saturated here. */
function GateCell({ c }) {
  if (c.gate_result === "PASS_LETC") return <RuleResult result="PASS" title="Passes L/E/T/C">PASS L/E/T/C</RuleResult>;
  if (c.gate_result === "WAIT") return <DecisionBadge decision="WAIT" size="compact" muted />;
  if (c.gate_result === "NO_TRADE") return <DecisionBadge decision="NO TRADE" size="compact" muted />;
  return <span className="sn-small sn-faint">{c.gate_result || "—"}</span>;
}

/**
 * The ranked candidate blotter for one run, with filters and row expansion (full L → B audit + payoff).
 * Each row's chevron is a real button, so expansion works from the keyboard (Tab, Enter/Space).
 */
export function CandidateBlotter({ query, cp, run, stub, mobile, width }) {
  const [fStruct, setFStruct] = useState("");
  const [fWidth, setFWidth] = useState("");
  const [fGate, setFGate] = useState("");
  const e1 = cp?.e1_floor != null ? cp.e1_floor * 100 : 8;
  const primaryDecision = normDecision(cp?.decision_label) === "ENTER" ? "ENTER" : "OTHER";
  const numCell = (v, kind, opts) => (stub ? <Num value={v} kind={kind} signed={opts?.signed} state="stub" /> : fmt(v, kind, opts));

  const columns = [
    { key: "rank", label: "#", num: true, pri: 1, w: 36, render: (r) => <span className="sn-rank">{r.rank}</span> },
    {
      key: "id", label: "Candidate", pri: 1, sticky: true,
      render: (r) => (
        <span className="sn-row" style={{ gap: 6, flexWrap: "nowrap" }}>
          <span className="sn-num" style={{ fontWeight: 500, color: "var(--text-primary)", fontSize: 12 }}>{shortId(r.candidate_id)}</span>
          {r.is_recalc && <span data-testid={`candidate-recalc-${r.rank}`} title="Appended by RECALC — the original run row is elsewhere in the list"><DataStateBadge state="RECALC" size="md" /></span>}
        </span>
      ),
    },
    { key: "strat", label: "Structure", pri: 2, render: (r) => <span style={{ color: "var(--text-primary)" }}>{r.strat} <span className="sn-faint sn-num">{r.structure}</span></span> },
    { key: "w", label: "Width", num: true, pri: 3, render: (r) => r.width },
    { key: "strikes", label: "Short / long", num: true, pri: 1, sortVal: (r) => r.short_strike, render: (r) => `${fmt(r.short_strike, "strike")} / ${fmt(r.long_strike, "strike")}` },
    { key: "credit", label: "Net", num: true, pri: 2, render: (r) => numCell(r.net_price, "price") },
    { key: "evUsd", label: "EV", num: true, pri: 3, render: (r) => numCell(r.evUsd, "price", { signed: true }) },
    {
      key: "ev", label: "EV %", num: true, pri: 1,
      render: (r) => (
        <span style={{ color: r.ev != null && r.ev < e1 ? "var(--validation-fail)" : "var(--text-primary)" }}>
          {fmt(r.ev, "pct", { signed: true })}{r.ev != null && r.ev < e1 && <span className="sn-vh"> below the E1 floor</span>}
        </span>
      ),
    },
    { key: "pp", label: "P(profit)", num: true, pri: 2, render: (r) => numCell(r.p_profit, "prob") },
    {
      key: "p_touch", label: "P(touch)", num: true, pri: 3, sortVal: (r) => r.p_touch ?? -1,
      render: (r) => (
        <span className="sn-row" style={{ gap: 6, flexWrap: "nowrap", justifyContent: "flex-end" }}>
          {r.p_touch == null ? "—" : fmt(r.p_touch, "prob")}
          {(r.warning_flags || []).includes("CREDIT_TAIL_SOFT") && (
            <span data-testid={`candidate-tail-soft-${r.rank}`} title="P(touch) > 25% or P(close beyond) > 10% — soft warning"><Badge icon="Info">SOFT</Badge></span>
          )}
        </span>
      ),
    },
    {
      key: "diagnostic_score", label: "Diag", num: true, pri: 3, sortVal: (r) => r.diagnostic_score ?? -1,
      render: (r) => <span data-testid={`candidate-diag-${r.rank}`} title="Diagnostic 0–100 quality score — display only, does NOT override gates">{r.diagnostic_score ?? "—"}</span>,
    },
    { key: "gate_result", label: "Gate", pri: 2, render: (r) => <GateCell c={r} /> },
    {
      key: "block", label: "Block", pri: 1, sortVal: (r) => r.block || "",
      render: (r) => (r.block ? (
        <span className="sn-row" style={{ gap: 6, flexWrap: "nowrap" }}><RuleResult result="FAIL" /><span className="sn-num" style={{ fontSize: 12, color: "var(--text-primary)", fontWeight: 600 }}>{r.block}</span></span>
      ) : <span className="sn-faint">—</span>),
    },
    { key: "n_fail_total", label: "Fails", num: true, pri: 2, render: (r) => r.n_fail_total },
  ];

  const renderExpanded = (c, mode) => (
    <div className="sn-stack" data-testid={`candidate-audit-${c.rank}`}>
      <div className="sn-small sn-muted" style={{ overflowWrap: "anywhere" }}>
        <span className="sn-num" style={{ color: "var(--text-primary)", fontWeight: 500 }}>{c.candidate_id}</span> · model {c.model_name} · sigma <span className="sn-num">{fmt(c.model_params?.sigma, "pts")}</span> · breakeven <span className="sn-num">{fmt(c.breakeven)}</span> · max profit <span className="sn-num">{fmt(c.max_profit, "usd")}</span> · max loss <span className="sn-num">{fmt(c.max_loss, "usd")}</span> · diag <span className="sn-num">{c.diagnostic_score ?? "—"}/100</span>
      </div>
      {c.model_name === "iv-surface-v1" && (
        <div className="sn-small sn-muted" style={{ overflowWrap: "anywhere" }} data-testid={`iv-surface-${c.rank}`}>
          <span className="sn-micro" style={{ marginRight: 6 }}>IV skew</span>
          ATM IV <span className="sn-num">{fmt((c.model_params?.iv_atm ?? 0) * 100, "pct")}</span> · at short <span className="sn-num">{fmt((c.model_params?.iv_short ?? 0) * 100, "pct")}</span> · at breakeven <span className="sn-num">{fmt((c.model_params?.iv_breakeven ?? 0) * 100, "pct")}</span>
        </div>
      )}
      {(c.warning_flags || []).length > 0 && (
        <div className="sn-row sn-small sn-muted" style={{ flexWrap: "nowrap", alignItems: "flex-start" }}>
          <Icon name="Info" size={14} style={{ color: "var(--validation-unconfirmed)", marginTop: 1 }} />
          <span>Warnings: <span className="sn-num">{(c.warning_flags || []).join(", ")}</span></span>
        </div>
      )}
      <div className="sn-grid-12">
        <div className={mode === "cards" ? "sn-col-12" : "sn-col-7"} style={{ minWidth: 0 }}>
          <AuditRow rules={c.rules} candidate={c} checkpoint={cp} stacked={mode === "cards"} />
        </div>
        <div className={mode === "cards" ? "sn-col-12" : "sn-col-5"} style={{ minWidth: 0 }}>
          <PayoffDiagram candidate={c} />
        </div>
      </div>
      <div className="sn-small sn-faint sn-num" style={{ overflowWrap: "anywhere" }}>
        Legs: {(c.legs || []).map((l) => `${l.right}${l.strike} bid ${l.bid}/ask ${l.ask} OI ${l.oi} vol ${l.volume}`).join("  ·  ")}
      </div>
    </div>
  );

  // Clicking anywhere on a row also expands it (keyboard users use the row's chevron button).
  const onTableClick = (e) => {
    if (e.target.closest("button, a, input, select, label, .sn-exp")) return;
    const tr = e.target.closest('tr[data-testid^="candidate-row-"]');
    tr?.querySelector(".sn-exp-btn")?.click();
  };

  return (
    <QueryBoundary query={query} what={`the candidates for Run ${run}`} errorTestId="candidates-error"
      isEmpty={(d) => !(d?.candidates || []).length}
      empty={{ icon: "ListOrdered", title: "No candidates at this run", body: `Run ${run} produced no candidate spreads.`, testId: "candidates-empty" }}>
      {(data) => {
        let cands = data.candidates || [];
        if (fStruct) cands = cands.filter((c) => c.structure === fStruct);
        if (fWidth) cands = cands.filter((c) => String(c.width) === fWidth);
        if (fGate) cands = cands.filter((c) => c.gate_result === fGate);
        const rows = cands.map((c, i) => ({
          ...c,
          _key: `${c.candidate_id}-${i}`,
          id: c.candidate_id,
          strat: STRUCTS[c.structure] || c.structure_name,
          w: c.width,
          short: c.short_strike,
          long: c.long_strike,
          credit: Math.abs(Number(c.net_price) || 0),
          risk: c.max_loss,
          pp: c.p_profit,
          ev: c.ev_pct != null ? c.ev_pct * 100 : null,
          evUsd: c.ev,
          block: c.blocking_rule || null,
          dec: c.is_primary && primaryDecision === "ENTER" ? "ENTER" : c.gate_result === "WAIT" ? "WAIT" : c.gate_result === "NO_TRADE" ? "NO TRADE" : undefined,
        }));
        const primaryId = rows.find((r) => r.is_primary)?.id;
        const modeled = cands.some((c) => (c.legs || []).some((l) => l.modeled));
        const footer = (
          <span data-testid="candidates-footer-note">
            <span className="sn-num">{cands.length}</span> candidates · ranked by net EV, then P(profit){modeled && " · some marks modelled from the day close"}
          </span>
        );
        return (
          <div className="sn-stack">
            <div className="sn-row no-print" style={{ gap: "var(--space-3)", alignItems: "flex-end" }}>
              <FilterSelect label="Structure" value={fStruct} onChange={setFStruct} options={[["", "All"], ["A", "A · Bear put"], ["B", "B · Bull call"], ["C", "C · Bull put credit"]]} testId="filter-structure" />
              <FilterSelect label="Width" value={fWidth} onChange={setFWidth} options={[["", "All"], ["5", "5"], ["10", "10"], ["15", "15"]]} testId="filter-width" />
              <FilterSelect label="Gate" value={fGate} onChange={setFGate} options={[["", "All"], ...Object.entries(GATES)]} testId="filter-gate" />
            </div>
            <div onClick={onTableClick}>
              <Blotter
                rows={rows}
                columns={columns}
                primaryId={primaryId}
                primaryDecision={primaryDecision}
                evThreshold={e1}
                mode={mobile ? "cards" : "table"}
                maxPriority={width < 1024 ? 2 : 3}
                renderExpanded={renderExpanded}
                rowKey={(r) => r._key}
                rowTestId={(r) => `candidate-row-${r.rank}`}
                chips={[
                  { key: "pass", label: "Passing", test: (r) => r.gate_result === "PASS_LETC" },
                  { key: "wait", label: "WAIT", test: (r) => r.gate_result === "WAIT" },
                  { key: "blocked", label: "Blocked", test: (r) => r.gate_result === "NO_TRADE" },
                ]}
                footer={footer}
                caption={`Candidates at Run ${run}`}
                data-testid="candidates-table"
              />
              {mobile && <p className="sn-small sn-faint" style={{ margin: "var(--space-2) 0 0" }}>{footer}</p>}
            </div>
          </div>
        );
      }}
    </QueryBoundary>
  );
}
